import { Form, Select } from "antd";
import type { HTMLAttributes } from "react";
import { Fragment } from "react/jsx-runtime";
import { useIntl } from "react-intl";
import { FormCheckbox } from "~components/Form/FormCheckbox";
import { FormHelpSection, type FormSectionLabel } from "~components/Form/FormHelpSection";
import { FormInput } from "~components/Form/FormInput";
import { FormItem } from "~components/Form/FormItem";
import { FormSelect } from "~components/Form/FormSelect";
import { FormSelectYesNo } from "~components/Form/FormSelectYesNo";
import { SelectUniform } from "~components/SelectUniform/SelectUniform";
import { FixtureResultStatuses } from "~enums/fixtureResultStatuses";
import { UniformTypes } from "~enums/uniformTypes";
import { useSportConfiguration } from "~hooks/useSportConfiguration";
import { useGetFixture } from "~pages/Fixtures/hooks";
import type { EditFixtureForm, EditFixtureFormKeys, FixturePersonTransformed } from "~pages/Fixtures/types";
import { useExternalIdValidator } from "../../hooks";
import { StyledEntrant, StyledEntrantName } from "../../styles";

type Props = HTMLAttributes<HTMLDivElement> & {
  isUpdating: boolean;
  isHome?: boolean;
};

export const FixtureParticipantsSection = ({ isUpdating, isHome = false, ...props }: Props) => {
  const form = Form.useFormInstance<EditFixtureForm>();
  const { formatMessage } = useIntl();

  const { uniforms } = useSportConfiguration();
  const isUniformEnabled = Object.entries(uniforms).length > 0;

  const { data: fixtureData, isFetching } = useGetFixture();

  const participantsKey: EditFixtureFormKeys = "participants";
  const externalIdRule = useExternalIdValidator(participantsKey);

  if (!fixtureData) return null;

  const participants: (FixturePersonTransformed & { index: number })[] = fixtureData.formData.participants
    .map((x, index) => ({ ...x, index }))
    .filter((x) => x.isHome === isHome);

  const fields: FormSectionLabel[] = [
    {
      titleIntlKey: "fixture.participant.name",
      descriptionIntlKey: "form.help.fixture.participant.name",
    },
    {
      titleIntlKey: "fixture.participant.abbreviation",
      descriptionIntlKey: "form.help.fixture.participant.abbreviation",
    },
    {
      titleIntlKey: "resultStatus",
      descriptionIntlKey: "form.help.fixture.resultStatus",
    },
    {
      titleIntlKey: "uniform",
      descriptionIntlKey: "form.help.fixture.uniform",
    },
    {
      titleIntlKey: "externalId",
      descriptionIntlKey: "form.help.fixture.externalId",
    },
    {
      titleIntlKey: "includeInRepresentation",
      descriptionIntlKey: "form.help.fixture.competitor.includeInRepresentation",
    },
  ];

  const sectionTitle = formatMessage(
    {
      id: isHome ? "fixture.update.participant.home.details" : "fixture.update.participant.away.details",
    },
    { count: participants.length ?? 0 },
  );

  return participants.length ? (
    <div {...props}>
      <FormHelpSection sectionTitle={sectionTitle} modalTitle={sectionTitle} fields={fields} />
      <StyledEntrant>
        {participants.map((participant) => (
          <Fragment key={participant.personId}>
            <div grid-full-width="true">
              <StyledEntrantName>{participant.name}</StyledEntrantName>
            </div>
            <FormCheckbox<EditFixtureForm> hidden name={[participantsKey, participant.index, "isHome"]} />
            <FormInput<EditFixtureForm> hidden name={[participantsKey, participant.index, "personId"]} />
            <FormInput<EditFixtureForm>
              label={formatMessage({ id: "fixture.participant.name" })}
              name={[participantsKey, participant.index, "name"]}
              disabled
            />
            <FormInput<EditFixtureForm>
              label={formatMessage({
                id: "fixture.participant.abbreviation",
              })}
              name={[participantsKey, participant.index, "nameAbbreviation"]}
              disabled
            />
            <FormSelect<EditFixtureForm>
              name={[participantsKey, participant.index, "resultStatus"]}
              label={formatMessage({ id: "resultStatus" })}
              disabled={isUpdating || isFetching}
            >
              {Object.values(FixtureResultStatuses).map((status) => (
                <Select.Option key={status} value={status}>
                  {status}
                </Select.Option>
              ))}
            </FormSelect>
            <FormInput<EditFixtureForm>
              label={formatMessage({ id: "externalId" })}
              name={[participantsKey, participant.index, "externalId"]}
              disabled={isUpdating || isFetching}
              rules={externalIdRule}
              inputProps={{
                onChange: () => {
                  // trigger validation for all externalIds (fixes bug when changed one externalId and another one is keep being invalid)
                  const formParticipants: FixturePersonTransformed[] = form.getFieldValue(participantsKey);
                  const allExternalIdFields = formParticipants.map((_, idx) => [participantsKey, idx, "externalId"]);
                  form.validateFields(allExternalIdFields);
                },
              }}
            />
            {isUniformEnabled && (
              <FormItem<EditFixtureForm>
                layout="vertical"
                label={formatMessage({ id: "uniform" })}
                name={[participantsKey, participant.index, "uniform"]}
              >
                <SelectUniform baseId={participant.personId} uniformType={UniformTypes.Person} disabled={isUpdating || isFetching} />
              </FormItem>
            )}
            <FormSelectYesNo<EditFixtureForm>
              label={formatMessage({ id: "includeInRepresentation" })}
              name={[participantsKey, participant.index, "includeInRepresentation"]}
              disabled={isUpdating || isFetching}
            />
          </Fragment>
        ))}
      </StyledEntrant>
    </div>
  ) : (
    <div />
  );
};
